/**
 * O portão das telas da jornada de SPOT.
 *
 * ## O defeito que ele fecha
 *
 * As rotas da jornada (`indicacoes/spot/:id`, `.../contrato` e a etapa 2 do
 * formulário) liam `featureFlags.ff_enable_partner_payment_flow` e **ignoravam**
 * o `loading` do mesmo hook. O mapa de flags nasce com TUDO `false` e só é
 * preenchido quando o PostHog responde (ver `FeatureFlagContext`), então no
 * primeiro render depois de um F5 a flag é falsa por AUSÊNCIA de resposta, não
 * por decisão — e o `<Navigate replace>` disparava.
 *
 * O efeito para o parceiro: recarregar a página no meio da jornada o jogava em
 * `/parceiros/indicacoes/spot` e apagava a URL do histórico (`replace`), sem
 * botão de voltar. Quem estava preenchendo os dados do contrato perdia o
 * formulário inteiro por um piscar de olhos de latência do PostHog.
 *
 * Por isso a primeira linha é `if (loading) return null`, **antes** de ler flag
 * nenhuma — o mesmo desenho de `EspelhoVendasGate` e `ChannelManagerGate`. É o
 * que torna os três estados observáveis por teste: nada enquanto carrega, o
 * quadro quando a flag está desligada, a tela quando está ligada.
 *
 * ## Por que um componente, e não um `&&` na rota
 *
 * As três rotas repetiam o mesmo ternário. Repetido, cada uma podia esquecer o
 * `loading` por conta própria — foi exatamente o que aconteceu. Com o portão, a
 * regra existe uma vez e as rotas só a vestem.
 */
import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { FeatureFlagsOptions } from '@/hooks/useFeatureFlags/types';

import { SPOT_LIST_PATH } from '@/pages/Partners/pages/Indicate/SpotChoice/caminhos';

interface IPortaoDaJornada {
  children: ReactElement;
  /**
   * Para onde mandar quem chega com a flag desligada. O padrão é o quadro de
   * indicações de SPOT, que é o que as rotas já faziam.
   */
  destinoSemFlag?: string;
}

export const PortaoDaJornada = ({
  children,
  destinoSemFlag = SPOT_LIST_PATH,
}: IPortaoDaJornada) => {
  const { featureFlags, loading } = useFeatureFlags();

  /* Antes de qualquer leitura de flag: o mapa vazio de um render de carga não é
     resposta, é ausência de resposta. */
  if (loading) return null;

  if (!featureFlags[FeatureFlagsOptions.FF_ENABLE_PARTNER_PAYMENT_FLOW]) {
    return <Navigate to={destinoSemFlag} replace />;
  }

  return children;
};

export default PortaoDaJornada;
